import { faRobot, faSearch } from "@fortawesome/pro-regular-svg-icons";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { v4 } from "uuid";

import { ErrorBaseInfo } from "src/api/types";
import { Button } from "src/base-components/Button";
import { Icon } from "src/base-components/Icon";
import { NodeConversationContextContextTypeEnum } from "src/clients/copilot";
import { BeMappedNode } from "src/constants/NodeDataTypes";
import { NODE_TYPE } from "src/constants/NodeTypes";
import { NodeSuggestion } from "src/copilot/CodeNodeSuggestion";
import { ConversationMessageList } from "src/copilot/ConversationMessageList";
import { NodePill } from "src/copilot/NodePill";
import { PromptBox } from "src/copilot/PromptBox";
import { useAskCopilot, useConversation } from "src/copilot/queries";
import { FloatingWindow } from "src/datasets/DatasetTable/FloatingWindow";
import {
  NoCompletionDataAvailable,
  useNodeCompletionData,
} from "src/nodeEditor/api/queries";
import { useAuthoringContext } from "src/router/routerContextHooks";

type Props = {
  anchor: React.ReactNode;
  error?: ErrorBaseInfo;
  node: BeMappedNode;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onApply: (suggestion: NodeSuggestion) => void;
};

const Suggestion: React.FC<{
  prompt: string;
  onClick: (prompt: string) => void;
}> = ({ prompt, onClick }) => {
  return (
    <button
      className="flex h-28 w-36 appearance-none flex-col items-start gap-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3 text-left font-inter-normal-12px"
      onClick={() => onClick(prompt)}
    >
      <Icon color="text-gray-500" icon={faSearch} size="xs" />
      <p className="text-gray-800">{prompt}</p>
    </button>
  );
};

const EmptyState: React.FC<{ onPromptClick: (prompt: string) => void }> = ({
  onPromptClick,
}) => (
  <div className="flex h-full flex-grow flex-col items-center justify-center gap-x-2 gap-y-3 p-2">
    <p className="text-gray-500 font-inter-normal-12px">Some ideas...</p>
    <div className="flex items-center gap-x-2">
      <Suggestion
        prompt="Calculate the applicant's age from their birth date"
        onClick={onPromptClick}
      />
      <Suggestion
        prompt="Explain what this code does"
        onClick={onPromptClick}
      />
      <Suggestion
        prompt="Add comments to this node's code for explainability"
        onClick={onPromptClick}
      />
    </div>
  </div>
);

export const CopilotButton: React.FC<{
  setIsOpen: (state: boolean) => void;
  disabled: boolean;
}> = ({ setIsOpen, disabled }) => {
  return (
    <Button
      disabled={disabled}
      iconLeft={faRobot}
      size="sm"
      variant="secondary"
      onClick={() => setIsOpen(true)}
    >
      <span className="whitespace-nowrap">Ask Copilot</span>
    </Button>
  );
};

export const CopilotWindow: React.FC<Props> = ({
  anchor,
  node,
  error,
  isOpen,
  setIsOpen,
  onApply,
}) => {
  const { workspace, version } = useAuthoringContext();
  const listRef = useRef<HTMLUListElement>(null);
  const conversationId = useMemo(() => v4(), []);
  const [prompt, setPrompt] = useState("");

  const { data: nodeTypingData } = useNodeCompletionData(
    version.id,
    node.id,
    node.type === NODE_TYPE.LOOP_NODE,
  );

  const { data } = useConversation({ conversationId });

  const { mutateAsync: askCopilot, isLoading } = useAskCopilot({
    conversationId,
    baseUrl: workspace.base_url!,
  });

  const messages = data?.messages ?? [];

  const autofillPrompt = () => {
    if (isOpen && prompt.length < 1 && error)
      setPrompt(`I'm receiving \`${error.msg}\`, can you suggest a fix?`);
  };

  useEffect(autofillPrompt, [error, prompt, isOpen]);

  const titleRight = (
    <>
      <div className="ml-auto flex max-w-28 items-center gap-x-3">
        <NodePill node={node} />
      </div>
      <span className="ml-2 text-gray-200">|</span>
    </>
  );

  const helpContent = (
    <div className="font-inter-normal-12px">
      <p>
        Copilot helps you create, edit, and debug nodes more efficiently. It can
        explain existing logic, suggest improvements, and assist with common
        coding tasks. Copilot is currently supported for:
      </p>
      <ul className="list-inside list-disc">
        <li>Code nodes</li>
        <li>Rule nodes</li>
        <li>Assignment nodes</li>
      </ul>
      <p>Usage limits apply. Human verification required for changes.</p>
      {/* TODO: https://linear.app/taktile/issue/AUTH-6177/update-link-to-copilot-docs */}
      <a
        className="underline font-inter-normal-13px"
        href="https://docs.taktile.com/references/copilot"
        rel="noreferrer"
        target="_blank"
      >
        Learn More
      </a>
    </div>
  );

  const handlePromptClick = async (prompt: string) => {
    await askCopilot({
      message: prompt,
      context: {
        node_definition: node,
        input_schema: version.input_schema,
        output_schema: version.output_schema,
        data_typing:
          nodeTypingData === NoCompletionDataAvailable
            ? undefined
            : nodeTypingData,
        context_type: NodeConversationContextContextTypeEnum.NODE,
      },
    });
  };

  const handleClose = () => {
    setIsOpen(false);
    setPrompt("");
  };

  useLayoutEffect(() => {
    if (listRef.current) {
      listRef.current.children[
        listRef.current.children.length - 1
      ].scrollIntoView({ behavior: "smooth" });
    }
  }, [messages.length]);

  return (
    <FloatingWindow
      button={anchor}
      height="h-150"
      helpText={helpContent}
      isOpen={isOpen}
      maximizable={false}
      pinnable={false}
      title="Taktile Copilot"
      titleRight={titleRight}
      lockNodeEditor
      onClose={handleClose}
    >
      <div className="flex h-full flex-col">
        {data ? (
          <ConversationMessageList
            ref={listRef}
            conversation={data}
            loading={isLoading}
            node={node}
            onApply={onApply}
          />
        ) : (
          <EmptyState onPromptClick={handlePromptClick} />
        )}
        <div className="border-t border-t-gray-200 px-2 py-4">
          <PromptBox
            disabled={isLoading}
            minified={messages.length > 0}
            value={prompt}
            onChange={(v) => setPrompt(v)}
            onSubmit={() => {
              if (prompt) {
                handlePromptClick(prompt);
                setPrompt("");
              }
            }}
          />
        </div>
      </div>
    </FloatingWindow>
  );
};
